import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import {
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine
} from 'recharts';
import ProductSelector from './ProductSelector';
import { apiPost } from '../services/apiClient';

// Monthly mean pricing sentiment on the native -10..+10 scale, from
// /topic/pricing/monthly (credibility-shrunk, source-blended, min 5
// posts/month). Sign is encoded by position around the zero line, so the
// columns stay single-hue; red/green appears only on the delta text with
// arrow glyphs as the secondary encoding.
const BAR_COLOR = '#2A78D6';
const POSITIVE_DELTA = '#27AE60';
const NEGATIVE_DELTA = '#E74C3C';

const WINDOW_OPTIONS = [
    { value: 12, label: 'Last 12 months' },
    { value: 24, label: 'Last 24 months' },
    { value: 36, label: 'Last 36 months' },
];

const PricingTrendChart = ({ topicMap, companyName }) => {
    const [data, setData] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [monthsBack, setMonthsBack] = useState(24);
    const [isLoading, setIsLoading] = useState(false);
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};
        if (Object.keys(topicMap).length > 0 && !selectedProduct) {
            const sortedTopics = Object.values(topicMap).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB;
            });
            if (sortedTopics[0]) {
                setSelectedProduct(sortedTopics[0].id);
            }
        }
        return topicMap;
    }, [topicMap, selectedProduct]);

    useEffect(() => {
        const fetchData = async () => {
            if (!selectedProduct) return;

            setIsLoading(true);
            try {
                const response = await apiPost('topic/pricing/monthly', {
                    topic_id: selectedProduct,
                    months_back: monthsBack,
                });

                if (!response.ok) {
                    throw new Error(`Backend error: ${response.status}`);
                }

                const json = await response.json();
                const rawData = json.data ?? json;

                if (!Array.isArray(rawData) || rawData.length === 0) {
                    setData([]);
                    return;
                }

                setData(rawData.map(item => ({
                    month: format(parseISO(item.month), 'MMM yy'),
                    fullMonth: item.month,
                    score: Number(item.mean_sentiment),
                    lowerCI: Number(item.lower_ci),
                    upperCI: Number(item.upper_ci),
                    count: Number(item.count),
                    delta: item.delta != null ? Number(item.delta) : null,
                })));
            } catch (error) {
                console.error('Error fetching pricing trend:', error);
                setData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [selectedProduct, monthsBack]);

    const stats = useMemo(() => {
        if (data.length === 0) return null;
        const latest = data[data.length - 1];
        const peak = data.reduce((a, b) => (b.score > a.score ? b : a));
        const low = data.reduce((a, b) => (b.score < a.score ? b : a));
        const totalPosts = data.reduce((sum, d) => sum + d.count, 0);
        return { latest, peak, low, totalPosts };
    }, [data]);

    const yDomain = useMemo(() => {
        if (data.length === 0) return [-10, 10];
        const values = data.flatMap(d => [d.score, d.lowerCI, d.upperCI]);
        const lo = Math.min(0, Math.floor(Math.min(...values)) - 1);
        const hi = Math.max(0, Math.ceil(Math.max(...values)) + 1);
        return [Math.max(lo, -10), Math.min(hi, 10)];
    }, [data]);

    const getSelectedProductName = () => {
        if (selectedProduct && filteredTopicMap && filteredTopicMap[selectedProduct]) {
            return filteredTopicMap[selectedProduct].name;
        }
        return 'Product';
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length > 0) {
            const d = payload[0].payload;
            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '10px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '13px'
                }}>
                    <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
                        {format(parseISO(d.fullMonth), 'MMMM yyyy')}
                    </p>
                    <p style={{ margin: '0 0 2px' }}>Pricing sentiment: <strong>{d.score.toFixed(2)}</strong></p>
                    <p style={{ margin: '0 0 2px', color: '#666' }}>95% CI: [{d.lowerCI.toFixed(2)}, {d.upperCI.toFixed(2)}]</p>
                    <p style={{ margin: 0, color: '#666' }}>Posts: {d.count.toLocaleString()}</p>
                </div>
            );
        }
        return null;
    };

    const CONTROL_HEIGHT = 36;

    const filterLabelStyle = {
        fontSize: '9px',
        fontWeight: 600,
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '3px'
    };

    const tileStyle = {
        flex: '1 1 0',
        minWidth: 120,
        backgroundColor: '#fafafa',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        borderRadius: '8px',
        padding: '12px 16px'
    };

    const tileLabelStyle = {
        fontSize: '10px',
        fontWeight: 600,
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '4px'
    };

    const tileValueStyle = {
        fontSize: '22px',
        fontWeight: 700,
        color: '#111',
        lineHeight: 1.2
    };

    const tileSubStyle = {
        fontSize: '11px',
        color: '#999',
        marginTop: '2px'
    };

    const renderDelta = (delta) => {
        if (delta == null || delta === 0) return null;
        const up = delta > 0;
        return (
            <span style={{
                fontSize: '12px',
                fontWeight: 600,
                marginLeft: '8px',
                color: up ? POSITIVE_DELTA : NEGATIVE_DELTA
            }}>
                {up ? '▲' : '▼'} {Math.abs(delta).toFixed(2)} MoM
            </span>
        );
    };

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box', marginBottom: '28px' }}>
            {/* Header Card */}
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    padding: '20px 24px 18px',
                    marginBottom: '16px',
                    boxShadow: isHeaderHovered
                        ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                        : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    transition: 'box-shadow 0.2s ease'
                }}
            >
                <div style={{ marginBottom: '12px' }}>
                    <div style={{
                        fontSize: '11px',
                        color: '#777',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        marginBottom: '1px'
                    }}>
                        {companyName || 'Company'}
                    </div>
                    <h1 style={{
                        fontSize: '22px',
                        fontWeight: 700,
                        margin: 0,
                        color: '#111',
                        lineHeight: 1.25
                    }}>
                        {selectedProduct
                            ? `${getSelectedProductName()} · Pricing Sentiment Over Time`
                            : 'Select a Product'}
                    </h1>
                    <div style={{
                        fontSize: '12px',
                        color: '#999',
                        lineHeight: 1.3,
                        marginTop: '2px'
                    }}>
                        Monthly mean of per-post pricing scores on a -10 to +10 scale.
                        Positive reads as tolerance for higher prices; negative as price resistance.
                    </div>
                </div>

                {/* Filter Bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '12px',
                    flexWrap: 'wrap',
                    rowGap: '12px'
                }}>
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 160 }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct || ''}
                            onSelect={setSelectedProduct}
                            label=""
                            size="small"
                        />
                    </div>
                    <div style={{ flex: '0 0 auto', minWidth: 140 }}>
                        <div style={filterLabelStyle}>Window</div>
                        <select
                            value={monthsBack}
                            onChange={(e) => setMonthsBack(Number(e.target.value))}
                            style={{
                                padding: '6px 10px',
                                borderRadius: '4px',
                                border: '1px solid #d0d0d0',
                                fontSize: '13px',
                                height: CONTROL_HEIGHT,
                                boxSizing: 'border-box',
                                width: '100%',
                                backgroundColor: 'white'
                            }}
                        >
                            {WINDOW_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Stat Tiles */}
            {stats && !isLoading && (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    <div style={tileStyle}>
                        <div style={tileLabelStyle}>Current</div>
                        <div style={tileValueStyle}>
                            {stats.latest.score.toFixed(1)}
                            {renderDelta(stats.latest.delta)}
                        </div>
                        <div style={tileSubStyle}>{format(parseISO(stats.latest.fullMonth), 'MMMM yyyy')}</div>
                    </div>
                    <div style={tileStyle}>
                        <div style={tileLabelStyle}>Peak</div>
                        <div style={tileValueStyle}>{stats.peak.score.toFixed(1)}</div>
                        <div style={tileSubStyle}>{format(parseISO(stats.peak.fullMonth), 'MMMM yyyy')}</div>
                    </div>
                    <div style={tileStyle}>
                        <div style={tileLabelStyle}>Low</div>
                        <div style={tileValueStyle}>{stats.low.score.toFixed(1)}</div>
                        <div style={tileSubStyle}>{format(parseISO(stats.low.fullMonth), 'MMMM yyyy')}</div>
                    </div>
                    <div style={tileStyle}>
                        <div style={tileLabelStyle}>Posts Analyzed</div>
                        <div style={tileValueStyle}>{stats.totalPosts.toLocaleString()}</div>
                        <div style={tileSubStyle}>Across {data.length} months</div>
                    </div>
                </div>
            )}

            {/* Chart */}
            <div style={{ width: '100%', height: 360 }}>
                {isLoading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        color: '#666'
                    }}>
                        Loading pricing trend...
                    </div>
                ) : data.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        color: '#999',
                        fontSize: '14px'
                    }}>
                        Not enough pricing assessments to chart a monthly trend.
                    </div>
                ) : (
                    <ResponsiveContainer>
                        <BarChart
                            data={data}
                            margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                            barCategoryGap="18%"
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                            <XAxis
                                dataKey="month"
                                tick={{ fontSize: 11, fill: '#666' }}
                                tickLine={false}
                                axisLine={{ stroke: '#d0d0d0' }}
                                interval="preserveStartEnd"
                            />
                            <YAxis
                                domain={yDomain}
                                tick={{ fontSize: 12, fill: '#666' }}
                                tickLine={false}
                                axisLine={false}
                                label={{
                                    value: 'Pricing sentiment (-10 to +10)',
                                    angle: -90,
                                    position: 'insideLeft',
                                    style: { textAnchor: 'middle', fontSize: 12, fill: '#999' }
                                }}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} />
                            <ReferenceLine y={0} stroke="#888" strokeWidth={1} />
                            <Bar dataKey="score" name="Pricing sentiment" fill={BAR_COLOR} maxBarSize={38}>
                                {data.map((d) => (
                                    <Cell
                                        key={d.fullMonth}
                                        fill={BAR_COLOR}
                                        radius={d.score >= 0 ? [3, 3, 0, 0] : [0, 0, 3, 3]}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default PricingTrendChart;
